import { computed, ref } from 'vue';
import { useRefineStore } from '@/store/refine.js';

const formatShort = (dateStr) => {
  if (!dateStr) return '';

  const date = new Date(dateStr);
  return date.toLocaleDateString('en-GB', { day: 'numeric', month: 'short' });
};

const getDaysCount = (start, end) => {
  const msPerDay = 1000 * 60 * 60 * 24;
  const days = Math.round((new Date(end).getTime() - new Date(start).getTime()) / msPerDay) + 1;

  return `(${days} day${days > 1 ? 's' : ''})`;
};

const getDateDisplayText = (tempDate, selectedDate) => {
  if (tempDate?.tempStartDate && tempDate?.tempEndDate) {
    return `${formatShort(tempDate.tempStartDate)} - ${formatShort(tempDate.tempEndDate)} ${getDaysCount(tempDate.tempStartDate, tempDate.tempEndDate)}`;
  }

  if (tempDate?.tempStartDate) {
    return formatShort(tempDate.tempStartDate);
  }

  if (selectedDate?.startDate && selectedDate?.endDate) {
    return `${formatShort(selectedDate.startDate)} - ${formatShort(selectedDate.endDate)} ${getDaysCount(selectedDate.startDate, selectedDate.endDate)}`;
  }

  if (selectedDate?.startDate) {
    return formatShort(selectedDate.startDate);
  }

  return 'Select trip dates';
};

export function useTripFinderForm() {
  const refine = useRefineStore();
  
  const selectedIWant = ref('build');
  const selectedGo = ref('local');
  const selectedDestination = ref(null);

  const selectedDate = ref(null);
  const tempDate = ref(null);
  const showDatePicker = ref(false);

  const dateDisplayText = computed(() => getDateDisplayText(tempDate.value, selectedDate.value));

  const canSearch = computed(() => {
    if (selectedIWant.value === 'build') {
      return !!selectedDestination.value;
    }

    if (selectedIWant.value === 'recommend') {
      return true;
    }

    return false;
  });

  const isRefineEnabled = computed(() => {
    if (selectedIWant.value === 'build') {
      return !!selectedDestination.value;
    }

    return true;
  });

  const isSearchEnabled = computed(() => canSearch.value && refine.refineValidation.isValidForSearch);

  const searchPayload = computed(() => ({
    iWant: selectedIWant.value,
    go: selectedGo.value,
    destination: selectedDestination.value,
    whereWhen: refine.selectedQuickDate || 'not-sure',
    date: selectedDate.value,
    tripDirection: refine.tripDirection,
    budget: refine.budgetAmount,
    travelers: refine.travelersData,
    themes: refine.selectedThemes,
    activities: refine.selectedActivities,
    travelMethods: refine.selectedTravelMethods,
    accommodations: refine.selectedAccommodations,
    specialRequirements: refine.selectedSpecialRequirements,
    tripTypes: refine.selectedTripTypes,
    discoverTripTypes: refine.selectedTripTypesDiscover,
    regions: refine.selectedRegions,
    distanceType: refine.selectedDistanceType,
    travelDistance: refine.travelDistanceValue,
    whoIsGoing: refine.selectedWhoIsGoing,
  }));

  const toggleDatePicker = () => {
    showDatePicker.value = !showDatePicker.value;
  };

  const setSelectedDestination = (locationData) => {
    selectedDestination.value = locationData;
  };

  const selectGoOption = (option) => {
    selectedGo.value = option;
  };

  const selectIWantOption = (option) => {
    selectedIWant.value = option;

    if (option === 'recommend') {
      selectedDestination.value = null;
    }

    if (option === 'build') {
      selectedGo.value = 'local';
    }
  };

  const handleDateSelected = (dateData) => {
    selectedDate.value = dateData;
    tempDate.value = { tempStartDate: null, tempEndDate: null };
    showDatePicker.value = false;
  };

  const handleTempDateChanged = (tempDateData) => {
    tempDate.value = tempDateData;
  };

  const clearDates = () => {
    selectedDate.value = null;
    tempDate.value = null;
    showDatePicker.value = false;
  };

  const buildSearchPayload = () => searchPayload.value;

  return {
    buildSearchPayload,
    canSearch,
    clearDates,
    dateHeaderText: dateDisplayText,
    desktopDateDisplayText: dateDisplayText,
    handleDateSelected,
    handleTempDateChanged,
    isRefineEnabled,
    isSearchEnabled,
    searchPayload,
    selectedDate,
    selectedDestination,
    selectedGo,
    selectedIWant,
    showDatePicker,
    tempDate,
    toggleDatePicker,
    selectGoOption,
    selectIWantOption,
    setSelectedDestination,
  };
}

export { getDateDisplayText };
